import React, { useState, useEffect } from "react";
import { Row, Col, Alert, Collapse, Statistic, Spin } from "antd";
import ActionBar from "../../../../components/ActionBar";
import { useWorkSummary } from "../../../../../hooks/useDataHooks";
import Loader from "../../Loader";
import { useReportDateRange } from "../../../../../hooks/useReportDateRange";
import API from "../../../../../API";
import ItemCard from "../../../../components/Summary/ItemCard";
import "./styles.css";

const { Panel } = Collapse;

const WorkSummaryPage = (props) => {
  const [packageData, setPackageData] = useState(null);
  const [packageSummaryData, setPackageSummaryData] = useState(null);
  const [packageLoading, setPackageLoading] = useState(true);
  const [summaryLoading, setSummaryLoading] = useState(true);
  const { startDate, endDate } = useReportDateRange();

  const {
    match: { params },
  } = props;

  const { packageId } = params;

  const contractId = packageData?.contract?.id;
  const {
    workSummary: data,
    loading: workSummaryLoading,
    error: workSummaryError,
  } = useWorkSummary(contractId, startDate, endDate);

  useEffect(() => {
    const fetchPackage = async () => {
      try {
        setPackageLoading(true);
        const response = await API.getPackage(packageId);
        setPackageData(response.data);
      } catch (err) {
        console.error("Failed to fetch package data", err);
      } finally {
        setPackageLoading(false);
      }
    };

    if (packageId) {
      fetchPackage();
    }
  }, [packageId]);

  useEffect(() => {
    const fetchPackageSummary = async () => {
      try {
        setSummaryLoading(true);
        const response = await API.get(`/procuring_entity_packages/${packageId}/summary-data`);
        console.log("response", response);
        setPackageSummaryData(response?.summary_data);
      } catch (err) {
        console.error("Failed to fetch package summary data", err);
        setPackageSummaryData(null);
      } finally {
        setSummaryLoading(false);
      }
    };

    if (packageId) {
      fetchPackageSummary();
    }
  }, [packageId]);

  const formatTitle = (str) => {
    return str
      .replace(/_/g, " ") 
      .replace(/\b\w/g, (char) => char.toUpperCase()); 
  };

  const formatValue = (value) => {
    if (value === null) return "-";

    if (typeof value === "string" && value.includes("T")) {
      const date = new Date(value);
      if (!isNaN(date.getTime())) {
        return date.toLocaleDateString(); 
      }
    }

    return value; 
  };

  const convertToArray = (inputData) => {
    let highLightsArray = Object.keys(inputData[0] || {}).map((key) => ({
      title: formatTitle(key),
      value: formatValue(inputData[0][key]), 
    }));

    let dataArray = Object.keys(inputData[1] || {}).map((key) => ({
      title: formatTitle(key),
      content: formatValue(inputData[1][key]), 
    }));

    return [dataArray, highLightsArray];
  };

  const getPackageSummaryItems = () => {
    const items = [];

    if (Array.isArray(packageSummaryData?.custom_items)) {
      packageSummaryData.custom_items.forEach((it) => {
        const visible = it?.visible !== false; 
        if (visible && it && it.label && it.value !== undefined) {
          items.push({
            label: it.label,
            value: it.value,
            prefix: it.prefix,
            suffix: it.suffix,
            format: it.format || 'raw',
            type: it.type || 'metric',
            color: it.color,
            bg: it.bg,
            width: it.width || 3,
          });
        }
      });
    }

    return items;
  };

  const progressHighlights = {
    time_elapsed: data?.time_elapsed_percent,
    planned_physical_progress: data?.planned_physical_progress,
    actual_physical_progress: data?.actual_physical_progress,
    planned_financial_progress: data?.planned_financial_progress_percent,
    actual_financial_progress: data?.actual_financial_progress_percent,
  };

  const allData = {
    material_mobilization: data?.material_mobilization,
    site_works: data?.site_works,
    quality_control: data?.quality_control,
    risk_management_and_mitigation: data?.risk_management_and_mitigation,
    mobilization_of_key_staff: data?.mobilization_of_key_staff,
    equipment_mobilization: data?.equipment_mobilization,
    financial_progress_amount: data?.financial_progress_amount?.amount,
    time_elapsed_months: data?.time_elapsed_months,
    time_elapsed_percent: data?.time_elapsed_percent,
    report_start_date: data?.report_start_date,
    report_end_date: data?.report_end_date,
  };

  const [dataArray, highLightsArray] = convertToArray([
    progressHighlights,
    allData,
  ]);

  const [activeKeys, setActiveKeys] = useState(["0"]);

  const handlePanelChange = (keys) => {
    setActiveKeys(keys);
  };

  const packageSummaryItems = getPackageSummaryItems();

  if (packageLoading) {
    return <Loader fontSize={50} />;
  }

  return (
    <div className="work-summary-page">
      <ActionBar
        actionButtonProp={{
          title: `${packageData?.name || ''} - Work Summary`,
        }}
      />

      <div style={{ marginBottom: '24px' }}>

        {summaryLoading ? (
          <Spin size="small" />
        ) : packageSummaryData?.headline || packageSummaryItems.length > 0 ? (
          <div>
            {packageSummaryData?.headline && (
              <div style={{ margin: '0 0 16px 0', fontSize: 14, fontWeight: 600 }}>
                {packageSummaryData.headline}
              </div>
            )}

            {packageSummaryItems.length > 0 && (
              <Row gutter={[8, 8]}>
                {packageSummaryItems.map((item, idx) => {
                  const clamp = (n, min, max) => Math.max(min, Math.min(max, n));
                  const w = clamp(Number(item.width || 3), 1, 4);
                  const mdSpan = Math.round(24 / w);

                  return (
                    <Col key={idx} xs={24} sm={12} md={mdSpan} lg={mdSpan} xl={mdSpan} xxl={mdSpan}>
                      <ItemCard item={item} />
                    </Col>
                  );
                })}
              </Row>
            )}
          </div>
        ) : (
          <Alert
            message="No Package Summary data provided for this package."
            type="info"
            showIcon
            style={{ margin: '8px 0' }}
          />
        )}
      </div>

      <div>
        <h3 style={{ margin: '16px 0', fontSize: 18, fontWeight: 600 }}>
          Work Summary
        </h3>

        {!contractId ? (
          <Alert
            message="No contract found for this package."
            type="warning"
            showIcon
            style={{ margin: '8px 0' }}
          />
        ) : workSummaryLoading ? (
          <Loader fontSize={16} />
        ) : workSummaryError ? (
          <p>{workSummaryError.message || "Failed to load work summary data"}</p>
        ) : !data || Object.keys(data).length === 0 ? (
          <p>No work summary available for this period.</p>
        ) : (
          <div>
            {highLightsArray && highLightsArray.length > 0 && (
              <div className="statistics-container subtle-box-shadow">
                {highLightsArray.map((item, index) => (
                  <Statistic
                    title={item.title}
                    value={`${item.value}%`}
                    key={index}
                    className={
                      item.title === "Planned Physical Progress" ||
                        item.title === "Planned Financial Progress"
                        ? "category-block"
                        : ""
                    }
                  />

                ))}
              </div>
            )}

            {dataArray && dataArray.length > 0 && (
              <Collapse
                bordered={false}
                defaultActiveKey={["0"]}
                expandIconPosition={"end"}
                className="collapse-menu subtle-box-shadow"
                onChange={handlePanelChange}
              >
                {dataArray.map((item, index) => (
                  <Panel
                    header={
                      <span>
                        {item.title}
                        {!activeKeys.includes(String(index)) && (
                          <span className="content-preview">
                            {" - " +
                              (item.content?.length > 40
                                ? item.content.substring(0, 40) + "..."
                                : item.content)}
                          </span>
                        )}
                      </span>
                    }
                    key={index}
                    className="collapse-header"
                  >
                    <p>{item.content}</p>
                  </Panel>
                ))}
              </Collapse>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default WorkSummaryPage;